/**
 * Componente: QueueItemRow
 * Linha expansível para itens da fila de processamento
 * Exibe payload, steps de execução e dados do contato
 */

import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ChevronDown, ChevronRight, RotateCcw, FileJson,
  ListOrdered, AlertCircle, User, Clock
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { QueueItemPgmq } from './QueueItemsTable';
import { QueueStepsTimeline, ExecutionStep } from './QueueStepsTimeline';
import { logger } from '@/lib/logger';

interface QueueItemRowProps {
  item: QueueItemPgmq;
  onRetry: (id: string) => void;
  retrying: boolean;
  duplicatedConversations?: Map<string, number>;
}

/**
 * Retorna badge de status visual
 */
function getStatusBadge(status: string) {
  switch (status) {
    case 'pending':
      return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Pendente</Badge>;
    case 'processing':
      return <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20">Processando</Badge>;
    case 'completed':
      return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">Completo</Badge>;
    case 'failed':
      return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">Falha</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

/**
 * Retorna badge de provider com cor
 */
function getProviderBadge(providerSlug: string | null) {
  const colorMap: Record<string, string> = {
    'z-api': 'border-green-500 text-green-500',
    'evolution': 'border-blue-500 text-blue-500',
    'chatwoot': 'border-orange-500 text-orange-500',
    'wuzapi': 'border-purple-500 text-purple-500',
    'notificame': 'border-cyan-500 text-cyan-500',
  };

  return (
    <Badge variant="outline" className={colorMap[providerSlug || ''] || ''}>
      {providerSlug?.toUpperCase() || 'N/A'}
    </Badge>
  );
}

export function QueueItemRow({ item, onRetry, retrying, duplicatedConversations }: QueueItemRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [steps, setSteps] = useState<ExecutionStep[]>([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [contactInfo, setContactInfo] = useState<{ name: string; phone: string } | null>(null);

  // Extrair conversation_id do payload para verificar duplicação
  const payload = item.payload as Record<string, unknown>;
  const innerPayload = payload?.payload as Record<string, unknown> | undefined;
  const conversationId = innerPayload?.conversation_id as string | undefined;
  const duplicateCount = conversationId && duplicatedConversations 
    ? duplicatedConversations.get(conversationId) 
    : undefined;

  // Buscar detalhes quando expandir
  useEffect(() => {
    if (!isOpen) return;

    const fetchDetails = async () => {
      setLoadingSteps(true);
      try {
        // Extrair dados do payload com tipagem segura
        const payload = item.payload as Record<string, unknown> | null;
        const innerPayload = payload?.payload as Record<string, unknown> | undefined;
        
        // Extrair IDs para buscar a execução correspondente
        const flowId = innerPayload?.flow_id as string | undefined;
        const conversationId = innerPayload?.conversation_id as string | undefined;
        const contactId = innerPayload?.contact_id as string | undefined;
        
        // Para payloads retry_node, o execution_id vem diretamente no payload
        const directExecutionId = innerPayload?.execution_id as string | undefined;

        let executionId: string | null = null;

        // Caso 1: retry_node - já temos o execution_id diretamente
        if (directExecutionId) {
          executionId = directExecutionId;
          logger.debug('QueueItemRow', `Usando execution_id direto: ${executionId}`);
        }
        // Caso 2: start_flow/process_response - buscar execução por flow_id + conversation_id
        else if (flowId && conversationId) {
          let query = supabase
            .from('flow_executions')
            .select('id, contact_id')
            .eq('flow_id', flowId)
            .eq('conversation_id', conversationId)
            .gte('started_at', item.created_at)
            .order('started_at', { ascending: true })
            .limit(1);
          
          // Adicionar filtro de contact_id se disponível
          if (contactId) {
            query = query.eq('contact_id', contactId);
          }
          
          const { data: execution, error: execError } = await query.maybeSingle();

          if (execError) {
            logger.error('QueueItemRow', `Erro ao buscar execução: ${execError.message}`);
          } else if (execution) {
            executionId = execution.id;
          }
        }

        // Se encontrou execution_id por qualquer método, buscar os steps
        if (executionId) {
          const { data: stepsData, error: stepsError } = await supabase
            .from('flow_execution_steps')
            .select('*')
            .eq('execution_id', executionId)
            .order('started_at', { ascending: true });

          if (stepsError) {
            logger.error('QueueItemRow', `Erro ao buscar steps: ${stepsError.message}`);
          } else {
            setSteps(stepsData as ExecutionStep[]);
          }
          
          // Para retry_node, buscar contact_id a partir da execução se não veio no payload
          if (!contactId && directExecutionId) {
            const { data: execData } = await supabase
              .from('flow_executions')
              .select('contact_id')
              .eq('id', executionId)
              .single();
            
            if (execData?.contact_id) {
              const { data: contact } = await supabase
                .from('contacts')
                .select('name, phone')
                .eq('id', execData.contact_id)
                .single();
              
              if (contact) {
                setContactInfo({ name: contact.name || 'Sem nome', phone: contact.phone || 'N/A' });
              }
            }
          }
        }

        // Buscar info do contato (usando contactId já declarado acima)
        if (contactId) {
          const { data: contact, error: contactError } = await supabase
            .from('contacts')
            .select('name, phone')
            .eq('id', contactId)
            .single();

          if (!contactError && contact) {
            setContactInfo({ name: contact.name || 'Sem nome', phone: contact.phone || 'N/A' });
          }
        }
      } catch (error) {
        logger.error('QueueItemRow', `Erro ao buscar detalhes: ${error}`);
      } finally {
        setLoadingSteps(false);
      }
    };

    fetchDetails();
  }, [isOpen, item.payload, item.created_at]);

  const hasPayload = payload && Object.keys(payload).length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <>
        {/* Linha principal */}
        <TableRow className={cn('hover:bg-muted/30', isOpen && 'bg-muted/20')}>
          <TableCell>{getStatusBadge(item.status)}</TableCell>
          <TableCell>
            {duplicateCount ? (
              <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20 font-mono text-xs">
                🔴 {duplicateCount}x
              </Badge>
            ) : (
              <span className="text-muted-foreground">─</span>
            )}
          </TableCell>
          <TableCell>{getProviderBadge(item.provider_slug)}</TableCell>
          <TableCell className="font-mono text-xs">{item.queue_type}</TableCell>
          <TableCell className="text-sm">{item.tenant_name}</TableCell>
          <TableCell className="text-sm">{item.flow_name}</TableCell>
          <TableCell>
            <span className={item.attempts > 1 ? 'text-yellow-500' : ''}>
              {item.attempts}/{item.max_attempts}
            </span>
          </TableCell>
          <TableCell className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(item.created_at), { 
              addSuffix: true,
              locale: ptBR 
            })}
          </TableCell>
          <TableCell className="text-xs">
            {item.processing_time 
              ? `${item.processing_time.toFixed(1)}s` 
              : '-'}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-1">
              {item.status === 'failed' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRetry(item.id)}
                  disabled={retrying}
                  className="h-7 w-7 p-0"
                >
                  <RotateCcw className={cn('h-4 w-4', retrying && 'animate-spin')} />
                </Button>
              )}
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </TableCell>
        </TableRow>

        {/* Conteúdo expandido */}
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/10 hover:bg-muted/10">
            <TableCell colSpan={10} className="p-0">
              <div className="p-4 space-y-4 border-t border-border/50">
                
                {/* Erro (se houver) */}
                {item.error_message && (
                  <div className="flex items-start gap-2 p-3 bg-red-500/10 rounded-md border border-red-500/20">
                    <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-600">Erro</p>
                      <p className="text-sm text-red-500">{item.error_message}</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Payload */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <FileJson className="h-4 w-4 text-primary" />
                      Payload
                    </div>
                    {hasPayload ? (
                      <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-x-auto text-muted-foreground max-h-[300px] overflow-y-auto">
                        {JSON.stringify(payload, null, 2)}
                      </pre>
                    ) : (
                      <p className="text-sm text-muted-foreground">Sem payload</p>
                    )}
                  </div>

                  {/* Steps de Execução */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <ListOrdered className="h-4 w-4 text-primary" />
                      Steps de Execução
                      {steps.length > 0 && (
                        <Badge variant="secondary" className="text-[10px]">
                          {steps.length} passos
                        </Badge>
                      )}
                    </div>
                    {loadingSteps ? (
                      <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                          <Skeleton key={i} className="h-10 w-full" />
                        ))}
                      </div>
                    ) : steps.length > 0 ? (
                      <div className="bg-muted/50 p-3 rounded-md max-h-[300px] overflow-y-auto">
                        <QueueStepsTimeline steps={steps} />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Nenhum step de execução encontrado
                      </p>
                    )}
                  </div>
                </div>

                {/* Info do Contato */}
                {contactInfo && (
                  <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-md">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div className="flex items-center gap-4 text-sm">
                      <span>
                        <span className="text-muted-foreground">Contato:</span>{' '}
                        <span className="font-medium">{contactInfo.name}</span>
                      </span>
                      <span>
                        <span className="text-muted-foreground">Telefone:</span>{' '}
                        <span className="font-mono">{contactInfo.phone}</span>
                      </span>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Criado: {format(new Date(item.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                  </span>
                  {item.processed_at && (
                    <span>
                      Processado: {format(new Date(item.processed_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                    </span>
                  )}
                </div>
              </div>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
}
